import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Mail, Phone } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { useLocale } from '../utils/localization';

const TermsPage = () => {
  const { currency, formatPrice } = useCurrency();
  const { locale } = useLocale(currency);
  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0E163B] to-[#1a2654] text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Terms of Service
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          <Badge className="bg-[#FF5A3D] text-white px-4 py-2">
            Version 1.0
          </Badge>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-12">
              <h2 className="text-xl font-semibold text-blue-900 mb-3">Quick Summary</h2>
              <p className="text-blue-800 text-sm">
                These terms govern your use of Local Business Heroes. By using our service, you agree to these terms. 
                We're committed to providing you with a great service while protecting both your rights and ours.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">1. Acceptance of Terms</h2>
            <p className="text-gray-600 mb-6">
              By accessing or using Local Business Heroes (“the Service”), you agree to be bound by these Terms of Service (“Terms”). 
              If you disagree with any part of these terms, then you may not access the Service.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">2. Description of Service</h2>
            <p className="text-gray-600 mb-4">
              Local Business Heroes provides an all-in-one business growth platform that includes:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>Lead capture and management tools</li>
              <li>Customer relationship management (CRM)</li>
              <li>Automated email and SMS marketing</li>
              <li>Online scheduling and booking system</li>
              <li>Review and reputation management</li>
              <li>Social media management tools</li>
              <li>Analytics and reporting features</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">3. User Accounts and Registration</h2>
            <p className="text-gray-600 mb-4">
              To use certain features of the Service, you must register for an account. When you create an account, you agree to:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Be responsible for all activities under your account</li>
              <li>Notify us immediately of any unauthorised use</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">4. Subscription Plans and Billing</h2>
            <p className="text-gray-600 mb-4">
              Local Business Heroes operates on a subscription basis with the following terms:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>Trial Period:</strong> We offer a 14-day trial for {formatPrice(5)} on all plans</li>
              <li><strong>Billing Cycles:</strong> Subscriptions are billed monthly or annually in advance</li>
              <li><strong>Auto-Renewal:</strong> Subscriptions automatically renew unless {locale.cancelled}</li>
              <li><strong>Price Changes:</strong> We may change prices with 30 days' notice</li>
              <li><strong>Refunds:</strong> Refunds are provided according to our refund policy</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">5. Cancellation and Termination</h2>
            <p className="text-gray-600 mb-4">
              You may cancel your subscription at any time through your account settings. Upon cancellation:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>You retain access until the end of your current billing period</li>
              <li>Your data will be preserved for 30 days after cancellation</li>
              <li>You can reactivate your account within 30 days with all data intact</li>
              <li>After 30 days, your data may be permanently deleted</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">6. Acceptable Use Policy</h2>
            <p className="text-gray-600 mb-4">
              You agree not to use the Service to:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>Send spam or unsolicited communications</li>
              <li>Upload malicious software or harmful content</li>
              <li>Violate any laws or regulations</li>
              <li>Infringe on others' intellectual property rights</li>
              <li>Attempt to gain unauthorised access to our systems</li>
              <li>Use the service for illegal or harmful activities</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">7. Data Protection and Privacy</h2>
            <p className="text-gray-600 mb-6">
              Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, 
              which is incorporated into these Terms by reference. We are committed to protecting your data in accordance with 
              GDPR and other applicable data protection laws.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">8. Service Availability</h2>
            <p className="text-gray-600 mb-6">
              While we strive to maintain high service availability, we do not guarantee uninterrupted access to the Service. 
              We may experience downtime for maintenance, updates, or due to circumstances beyond our control. We aim for 99.9% uptime 
              and will provide advance notice of scheduled maintenance when possible.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">9. Intellectual Property Rights</h2>
            <p className="text-gray-600 mb-6">
              The Service and its original content, features, and functionality are and will remain the exclusive property of 
              Local Business Heroes and its licensors. The Service is protected by copyright, trademark, and other laws. 
              You may not reproduce, distribute, or create derivative works without our express written permission.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">10. Limitation of Liability</h2>
            <p className="text-gray-600 mb-6">
              To the fullest extent permitted by law, Local Business Heroes shall not be liable for any indirect, incidental, 
              special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, 
              or any loss of data, use, goodwill, or other intangible losses.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">11. Changes to Terms</h2>
            <p className="text-gray-600 mb-6">
              We reserve the right to modify these Terms at any time. We will notify users of significant changes by email 
              or through the Service at least 30 days before the changes take effect. Your continued use of the Service after 
              changes take effect constitutes acceptance of the new Terms.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">12. Your Content and Customer Data</h2>
            <p className="text-gray-600 mb-4">
              <strong>Your Rights:</strong> You retain all ownership rights to any content you upload, create, or store using the Service ("Your Content"). 
              By using our Service, you grant us a limited license to process, store, and transmit Your Content solely for the purpose of 
              providing the Service to you.
            </p>
            <p className="text-gray-600 mb-4">
              <strong>Your Responsibilities:</strong> You are solely responsible for Your Content and customer data you process through the Service. You represent and warrant that:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>You have all necessary rights, consents, and permissions to upload and process Your Content</li>
              <li>Your Content complies with all applicable laws, including data protection, privacy, and anti-spam regulations</li>
              <li>You have obtained proper consent from your customers to collect and process their personal data</li>
              <li>Your use of SMS and email features complies with PECR, GDPR, CAN-SPAM, and other applicable regulations</li>
              <li>You will not use the Service to send unsolicited communications (spam)</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">13. SMS and Email Messaging Compliance</h2>
            <p className="text-gray-600 mb-4">
              When using our messaging features, you must comply with all applicable laws and regulations:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>Consent Required:</strong> You must obtain prior express written consent before sending marketing messages</li>
              <li><strong>Opt-Out Mechanisms:</strong> You must provide clear opt-out instructions in every message</li>
              <li><strong>Identification:</strong> Messages must clearly identify you as the sender</li>
              <li><strong>Message Rates:</strong> You are responsible for any carrier charges your recipients may incur</li>
              <li><strong>Prohibited Content:</strong> No illegal, fraudulent, or harmful content may be sent through our Service</li>
              <li><strong>Volume Limits:</strong> We may impose reasonable sending limits to prevent abuse</li>
            </ul>
            <p className="text-gray-600 mb-6">
              We reserve the right to suspend or terminate accounts that violate messaging regulations or engage in spam activities.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">14. Data Processing Agreement (DPA)</h2>
            <p className="text-gray-600 mb-4">
              When you use our Service to process personal data of your customers, we act as a data processor on your behalf, and you act as the data controller. 
              Our Data Processing Agreement (DPA) governs this relationship and is incorporated into these Terms. Key provisions include:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>We process personal data only on your documented instructions</li>
              <li>We maintain appropriate technical and organisational security measures</li>
              <li>We assist you in responding to data subject requests (access, deletion, portability)</li>
              <li>We notify you of any personal data breaches affecting your customer data</li>
              <li>We allow you to audit our compliance with GDPR and other data protection laws</li>
              <li>Upon termination, we return or delete customer data as you direct</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">15. Third-Party Integrations</h2>
            <p className="text-gray-600 mb-6">
              Our Service may integrate with third-party services (e.g., payment processors, social media platforms, communication providers). 
              Your use of third-party integrations is subject to their respective terms of service and privacy policies. We are not responsible 
              for the actions or policies of third-party services. You should review their terms before using any integration.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">16. Service Level and Support</h2>
            <p className="text-gray-600 mb-4">
              <strong>Uptime Target:</strong> We target 99.9% service availability, calculated monthly, excluding scheduled maintenance.
            </p>
            <p className="text-gray-600 mb-4">
              <strong>Support:</strong> Support availability varies by plan:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>Starter Plan:</strong> Email support during UK business hours (Monday-Friday, 9am-5pm GMT)</li>
              <li><strong>Growth Plan:</strong> Priority email and phone support, extended hours</li>
              <li><strong>Domination Plan:</strong> 24/7 priority support with dedicated account manager</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">17. Indemnification</h2>
            <p className="text-gray-600 mb-6">
              You agree to indemnify, defend, and hold harmless Local Business Heroes and its officers, directors, employees, and agents from any claims, 
              liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or in any way connected with: (a) your access to 
              or use of the Service; (b) Your Content; (c) your violation of these Terms; (d) your violation of any third-party rights, including privacy, 
              publicity, or intellectual property rights; or (e) any breach of your representations and warranties set forth in these Terms.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">18. Warranty Disclaimer</h2>
            <p className="text-gray-600 mb-6">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO 
              IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE 
              WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, OR THAT DEFECTS WILL BE CORRECTED. YOU USE THE SERVICE AT YOUR OWN RISK.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">19. Liability Cap</h2>
            <p className="text-gray-600 mb-6">
              EXCEPT WHERE PROHIBITED BY LAW, IN NO EVENT SHALL LOCAL BUSINESS HEROES' TOTAL LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS OR 
              THE SERVICE EXCEED THE GREATER OF (A) THE AMOUNTS PAID BY YOU TO US IN THE 12 MONTHS PRECEDING THE CLAIM, OR (B) £100. THIS LIMITATION 
              APPLIES TO ALL CAUSES OF ACTION IN AGGREGATE, INCLUDING BREACH OF CONTRACT, BREACH OF WARRANTY, NEGLIGENCE, STRICT LIABILITY, AND OTHER TORTS.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">20. Dispute Resolution and Governing Law</h2>
            <p className="text-gray-600 mb-4">
              <strong>Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws of England and Wales, 
              without regard to its conflict of law provisions.
            </p>
            <p className="text-gray-600 mb-4">
              <strong>Dispute Resolution:</strong> Any dispute arising from or relating to these Terms or the Service shall be resolved as follows:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>Negotiation:</strong> First, we'll attempt to resolve disputes through good-faith negotiations</li>
              <li><strong>Mediation:</strong> If negotiation fails, disputes may be submitted to mediation under the CEDR Model Mediation Procedure</li>
              <li><strong>Jurisdiction:</strong> Failing mediation, you agree to submit to the exclusive jurisdiction of the courts of England and Wales</li>
            </ul>
            <p className="text-gray-600 mb-6">
              <strong>EU Consumers:</strong> If you are a consumer based in the EU, you may also bring proceedings in your country of residence, 
              and nothing in these Terms affects your statutory rights under applicable consumer protection laws.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">21. Force Majeure</h2>
            <p className="text-gray-600 mb-6">
              We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including acts of God, 
              natural disasters, war, terrorism, riots, strikes, labour disputes, government actions, telecommunications failures, internet outages, 
              or failures of third-party service providers.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">22. Account Suspension and Termination</h2>
            <p className="text-gray-600 mb-4">
              We may suspend or terminate your account immediately if:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>You breach these Terms or our Acceptable Use Policy</li>
              <li>Your account is used for illegal activities</li>
              <li>You fail to pay fees when due (after 7-day grace period)</li>
              <li>Your use of the Service poses a security risk to us or other users</li>
              <li>We are required to do so by law or court order</li>
            </ul>
            <p className="text-gray-600 mb-6">
              Upon termination, your right to use the Service immediately ceases. We will provide reasonable opportunity to export Your Content 
              within 30 days of termination, after which it may be permanently deleted.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">23. Assignment</h2>
            <p className="text-gray-600 mb-6">
              You may not assign or transfer these Terms or your account without our prior written consent. We may assign these Terms or delegate 
              any of our obligations without restriction, including in connection with a merger, acquisition, or sale of assets.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">24. Entire Agreement and Severability</h2>
            <p className="text-gray-600 mb-6">
              These Terms, together with our Privacy Policy and any additional terms applicable to specific features, constitute the entire agreement 
              between you and Local Business Heroes regarding the Service. If any provision of these Terms is found to be unenforceable, the remaining 
              provisions will continue in full force and effect.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">25. Contact Information</h2>
            <p className="text-gray-600 mb-6">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail size={20} className="text-[#FF5A3D]" />
                  <span className="text-gray-700">legal@localbusinessheroes.net</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone size={20} className="text-[#FF5A3D]" />
                  <span className="text-gray-700">UK: +44 20 4634 9070</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone size={20} className="text-[#FF5A3D]" />
                  <span className="text-gray-700">USA: +1 866-540-6506 (Toll Free)</span>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                These Terms of Service are effective as of {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} 
                and will remain in effect except with respect to any changes in their provisions in the future, which will be 
                in effect immediately after being posted on this page.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <Card className="p-8 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] text-white">
            <CardContent className="pt-6">
              <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Ready to Get Started?
              </h2>
              <p className="text-xl opacity-90 mb-6">
                Now that you've read our terms, start your 14-day trial and see how Local Business Heroes can transform your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  asChild
                  className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-semibold px-8 py-3"
                >
                  <a href="/pricing">
                    Start 14-Day Trial for {formatPrice(5)}
                  </a>
                </Button>
                <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF5A3D]">
                  <a href="/contact">Contact Us</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;